import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { bestText, fetchRoomMeta, GAMES, PUBLIC_GAMES } from "../lib/room";
import { gamePath, pageSeo, useSeo } from "../lib/seo";
import { normalizeCode, ROOM_CODE_LENGTH } from "../lib/ids";
import { levelInfo, levelTitle } from "../lib/xp";
import FriendsPanel from "../components/FriendsPanel";
import AdSlot from "../components/AdSlot";
import { useCreateRoom, useQuickMatch } from "../components/CreateRoom";
import { Avatar, PopularTag, XPBar } from "../components/ui";
import { gameOrder, usePopular } from "../lib/popular";
import { mark, useI18n } from "../i18n";
import { StreamerSwitch } from "../components/Streamer";
import DiscordLink from "../components/DiscordLink";
import GameIcon from "../components/GameIcon";
import Pic from "../components/pics";

const GAME_DETAILS = {
  shuffle: {
    bullets: [mark("Can't decide? Play a random game with random rules"), mark("When it ends, roll another one or stop"), mark("Earn shuffle points in every game to become champion of the night")],
  },
  doodle: {
    bullets: [mark("Pick 1 of 3 word cards - harder cards score more"), mark("Accept a Twist card (Blindfold! Upside Down!) for bonus points"), mark("Guessers play Double Down & Time Warp cards")],
  },
  whoami: {
    bullets: [mark("Everyone sees your card pinned to your avatar - except you"), mark("Take turns asking yes / no / maybe questions"), mark("Guess any time - the faster you crack it, the more you score")],
  },
  tycoon: {
    bullets: [mark("Roll, move and buy the hottest party spots"), mark("Collect color sets, build houses & hotels, charge rent"), mark("Party Cards, a jackpot corner, and the dreaded Timeout Box")],
  },
  impostor: {
    bullets: [mark("Everyone gets the secret word - except the impostor"), mark("Take turns giving one-word clues without giving it away"), mark("Vote out the faker... who gets one last guess to steal the win")],
  },
  mafia: {
    bullets: [mark("Secret roles: Mafia, Doctor, Detective & Villagers"), mark("At night the Mafia strike, in their own private chat"), mark("By day, argue, accuse and vote someone out")],
  },
  codewords: {
    bullets: [mark("Split into Red vs Blue with a spymaster each"), mark("Spymasters give one-word clues to find their agents"), mark("Hit the other team's card and your turn ends - hit the assassin and you lose")],
  },
  telephone: {
    bullets: [mark("Everyone writes a silly prompt"), mark("It gets passed along: draw it, describe it, draw it again…"), mark("Watch every chain get revealed - and star the funniest")],
  },
  punchline: {
    bullets: [mark("Answer two silly prompts - each one goes to two players"), mark("Answers face off and everyone votes for the funnier one"), mark("Win every vote for a Clean Sweep, then the Last Laugh finale")],
  },
  bluffalo: {
    bullets: [mark("Read a weird true fact with a word missing"), mark("Write a fake answer that sounds real"), mark("Spot the truth - and score for every friend your lie fools")],
  },
  split: {
    bullets: [mark("Finish a would-you scenario in your own words"), mark("Everyone else votes yes or no"), mark("The closer to a perfect 50/50 split, the more you score")],
  },
  lastcard: {
    bullets: [mark("Match the top card by colour or number, or play a Wild"), mark("Skip, Reverse, +2 and +4 cards to mess with your friends"), mark("Down to one card? Shout Last card! before someone catches you")],
  },
  fakeartist: {
    bullets: [mark("Everyone gets the secret word - except the fake artist"), mark("Take turns adding one line each to the same drawing"), mark("Vote out the faker... who gets one guess to steal the win")],
  },
  vibedial: {
    bullets: [mark("One player sees a hidden target on a scale like Cold ↔ Hot"), mark("They give a clue, and everyone turns their own dial"), mark("Bullseye scores 400 - and the clue giver gets the average")],
  },
  rummy: {
    bullets: [mark("Draw a card from the pile or take the top discard"), mark("Lay down sets and runs, or add to melds on the table"), mark("Empty your hand first and score everyone else's cards")],
  },
};

const FAQ = [
  [
    mark("Is PartyCade free?"),
    mark("Yes. Every game is free to play. A few small ads in the menus keep it that way, and there are never ads during a round."),
  ],
  [
    mark("Do I need to download anything or make an account?"),
    mark("No. It runs in your browser. Pick a nickname and play as a guest, or make a free account to save your XP and add friends."),
  ],
  [
    mark("How many people can play?"),
    mark("Most games start at 2 players, and there's no maximum. Each game shows the group size it's best with."),
  ],
  [
    mark("Can we play over a video call or voice chat?"),
    mark(
      "Yes. Share the room link in your group chat, Discord, or any call. Everyone plays on their own screen while you talk. If your browser crashes, you can jump back into your seat within 5 minutes."
    ),
  ],
];

function GameCard({ game, onCreate, busy, popular }) {
  const { t, lang } = useI18n();
  const g = GAMES[game];
  const d = GAME_DETAILS[game];
  return (
    <article className={`game-card ${popular ? "popular" : ""}`} style={{ "--accent": g.color }}>
      {popular && <PopularTag />}
      <div className="game-card-art" aria-hidden>
        <i className="float-dot float-dot-0" />
        <GameIcon id={g.id} className="float float-1" />
        <i className="float-dot float-dot-2" />
      </div>
      <div className="game-card-body">
        <h2>
          <GameIcon id={g.id} /> {t(g.name)}
        </h2>
        <p className="tagline">{t(g.tagline)}</p>
        <p className="game-players">
        <Pic name="people" /> {bestText(g, t)}
      </p>
        <ul>
          {d.bullets.map((b) => (
            <li key={b}>{t(b)}</li>
          ))}
        </ul>
        <button className="btn btn-primary btn-lg btn-block" onClick={() => onCreate(game)} disabled={busy}>
          {busy ? t("Shuffling…") : t("Create a room")}
        </button>
        <Link to={gamePath(game, lang)} className="how-to-link">
          {t("How to play {game}", { game: t(g.name) })} <span className="flip-rtl">→</span>
        </Link>
      </div>
    </article>
  );
}

export default function Home() {
  const { t, tx, lang } = useI18n();
  const { user, profile, isGuest } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState("");
  const { create, busy, modal } = useCreateRoom();
  const quick = useQuickMatch();
  // Party Shuffle, then the 🔥 most popular games, then the rest.
  const top = usePopular();
  const games = gameOrder(Object.keys(PUBLIC_GAMES), top);
  useSeo({ ...pageSeo(lang, "/"), path: "/", lang });

  const join = async (e) => {
    e.preventDefault();
    const code = normalizeCode(joinCode);
    if (code.length !== ROOM_CODE_LENGTH) return;
    try {
      const meta = await fetchRoomMeta(code);
      if (!meta) {
        toast({ emoji: "search", title: t("Room not found"), body: t("Nothing at {code}. Check the code?", { code }) });
        return;
      }
    } catch {
      // Not signed in yet - the room page handles that.
    }
    navigate(`/r/${code}`);
  };

  const info = profile ? levelInfo(profile.xp) : null;

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-text">
          <h1>{tx("Game night, <squiggle>one link away.</squiggle>", {}, { squiggle: (c, k) => <span key={k} className="squiggle">{c}</span> })}</h1>
          <p>{t("Spin up a room, send the link to your friends, and play in seconds. No downloads. Guests welcome.")}</p>
          <form className="join-form" onSubmit={join}>
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(normalizeCode(e.target.value).slice(0, ROOM_CODE_LENGTH))}
              placeholder={t("ROOM CODE")}
              aria-label={t("Room code")}
            />
            <button className="btn btn-secondary btn-lg" disabled={joinCode.length !== ROOM_CODE_LENGTH}>
              {t("Join room")} <span className="flip-rtl">→</span>
            </button>
          </form>
          <div className="quick-match">
            <button className="btn btn-primary btn-lg" onClick={quick.go} disabled={quick.busy}>
              {quick.busy ? t("Finding a lobby…") : t("Quick match")}
            </button>
            <span className="muted small">{t("Play with random people. Lobbies are private unless the host opens them.")}</span>
          </div>
          {/* A full page load, so the TV tab gets its own sign-in (see lib/tvTab.js). */}
          <div className="hero-links">
            <a className="btn btn-ghost" href="/tv">
              <Pic name="tv" /> {t("Host on a TV")}
            </a>
            <DiscordLink variant="button" />
          </div>
          <StreamerSwitch />
        </div>
        <div className="hero-deck" aria-hidden>
          <div className="deck-card c1">
            <Pic name="palette" />
          </div>
          <div className="deck-card c2">
            <Pic name="cards" />
          </div>
          <div className="deck-card c3">
            <Pic name="dice" />
          </div>
        </div>
      </section>

      <div className="home-grid">
        <main className="home-main">
          <div className="games-row">
            {games.map((g) => (
              <GameCard key={g} game={g} onCreate={create} busy={busy === g} popular={top.includes(g)} />
            ))}
          </div>
          <AdSlot kind="banner" />
          <section className="how">
            <h2>{t("How it works")}</h2>
            <div className="how-steps">
              <div className="how-step">
                <span>1</span>
                <h3>{t("Make a room")}</h3>
                <p>{t("Pick a game. You get a 5-letter code and a link.")}</p>
              </div>
              <div className="how-step">
                <span>2</span>
                <h3>{t("Share the link")}</h3>
                <p>
                  {t("Send it anywhere - a group chat, a text, Discord. Friends click it and jump straight in, as a guest or with an account.")}
                </p>
              </div>
              <div className="how-step">
                <span>3</span>
                <h3>{t("Level up")}</h3>
                <p>{t("Earn XP for every minute you play. Show off your level to the squad.")}</p>
              </div>
            </div>
          </section>
          <section className="about panel">
            <h2>{t("Free online party games for you and your friends")}</h2>
            <p>
              {t(
                "PartyCade is a collection of free party games you play in your web browser: a drawing and guessing game, a Who Am I? guessing game, a party board game, social deduction games like Impostor and Mafia, a team word game, comedy games like Punchline Duel and Bluffalo, and the Last Card game. Make a room, send the link, and everyone joins from a phone, tablet or computer."
              )}
            </p>
            <nav className="about-games" aria-label={t("All games")}>
              {games.map((id) => (
                <Link key={id} to={gamePath(id, lang)}>
                  <GameIcon id={id} /> {t(GAMES[id].name)}
                </Link>
              ))}
            </nav>
            <div className="faq">
              {FAQ.map(([q, a]) => (
                <details key={q}>
                  <summary>{t(q)}</summary>
                  <p>{t(a)}</p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <aside className="home-side">
          {profile ? (
            <div className="panel profile-card">
              <div className="profile-card-top">
                <Avatar avatar={profile.avatar} size={56} level={info.level} />
                <div>
                  <strong>{profile.displayName}</strong>
                  <small>{t(levelTitle(info.level))}</small>
                </div>
              </div>
              <XPBar xp={profile.xp} />
            </div>
          ) : (
            user &&
            isGuest && (
              <div className="panel guest-nudge">
                <strong>{t("Playing as a guest")}</strong>
                <p className="muted">{t("Your XP won't be saved. Make an account to keep it - you'll stay in your rooms.")}</p>
              </div>
            )
          )}
          <FriendsPanel />
          <AdSlot kind="sidebar" />
        </aside>
      </div>

      {modal}
      {quick.modal}
    </div>
  );
}
